import React from "react";
import {
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Text,
  VStack,
} from "@chakra-ui/react";
import { IconBell } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useAdminNotifications } from "../../hooks/useAdminNotifications";

const notificationPath = (notification: { page?: string | null; page_id?: number | null }) => {
  if (!notification.page_id) return "/dashboard";
  if (notification.page === "applyonda") return `/applyonda/show/${notification.page_id}`;
  if (notification.page === "contact") return `/contact/show/${notification.page_id}`;
  return `/appraisal/show/${notification.page_id}`;
};

export const AdminNotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useAdminNotifications();

  const openNotification = async (notification: typeof notifications[number]) => {
    try {
      if (!notification.is_read) await markAsRead(notification.id);
    } finally {
      navigate(notificationPath(notification));
    }
  };

  return (
    <Popover placement="bottom-end">
      <PopoverTrigger>
        <Box position="relative">
          <IconButton
            aria-label="관리자 알림 열기"
            icon={<IconBell size={19} />}
            size="md"
            variant="ghost"
          />
          {unreadCount > 0 && (
            <Badge
              position="absolute"
              top="0"
              right="0"
              transform="translate(25%, -25%)"
              colorScheme="red"
              borderRadius="full"
              minW="18px"
              textAlign="center"
              fontSize="10px"
              aria-label={`읽지 않은 알림 ${unreadCount}건`}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </Box>
      </PopoverTrigger>
      <PopoverContent bg="var(--app-bg-card)" borderColor="var(--app-border)" maxW="360px">
        <PopoverArrow bg="var(--app-bg-card)" />
        <PopoverHeader borderColor="var(--app-border)">
          <HStack justify="space-between">
            <Text fontWeight="700" color="var(--app-text-primary)">알림</Text>
            <Button variant="ghost" size="xs" onClick={() => void markAllAsRead()} isDisabled={unreadCount === 0}>
              모두 읽음
            </Button>
          </HStack>
        </PopoverHeader>
        <PopoverBody maxH="360px" overflowY="auto" px={2}>
          {notifications.length === 0 ? (
            <Text py={8} textAlign="center" color="var(--app-text-secondary)" fontSize="sm">새 알림이 없습니다.</Text>
          ) : (
            <VStack align="stretch" spacing={1}>
              {notifications.map((notification) => (
                <Button
                  key={notification.id}
                  variant="ghost"
                  height="auto"
                  py={3}
                  px={3}
                  justifyContent="flex-start"
                  textAlign="left"
                  whiteSpace="normal"
                  bg={notification.is_read ? "transparent" : "var(--app-bg-table-header)"}
                  _hover={{ bg: "var(--app-bg-table-header)" }}
                  onClick={() => void openNotification(notification)}
                >
                  <Box minW={0}>
                    <Text color="var(--app-text-primary)" fontWeight={notification.is_read ? "500" : "700"} fontSize="sm" noOfLines={1}>
                      {notification.title}
                    </Text>
                    <Text color="var(--app-text-secondary)" fontSize="xs" noOfLines={2}>{notification.content}</Text>
                    <Text color="var(--app-text-secondary)" fontSize="10px" mt={1}>{dayjs(notification.created_at).format("MM월 DD일 HH:mm")}</Text>
                  </Box>
                </Button>
              ))}
            </VStack>
          )}
        </PopoverBody>
        <PopoverFooter borderColor="var(--app-border)" py={2}>
          <Text fontSize="xs" color="var(--app-text-secondary)">15초마다 새 접수를 확인합니다.</Text>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
};
